import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionItem,
  Badge,
  ChartingTabs,
  SectionCard,
  type BadgeColor,
} from '@enamelai/design-system'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { Icon as TxPlanIcon } from '@enamelai/design-system/icons/Tx_plan'
import {
  IPhaseItems,
  IPhases,
  ITreatmentPlan,
  TX_PLAN_STATUS,
  TxPlanStatus,
} from '@dataTypes/treatmentPlan.types'
import { getFullName } from '@utils/fullname.util'
import { formatNumber } from '@utils/numberFormatter.util'
import {
  getTSFromToothSurfaces,
  parseSurfaceString,
} from '@utils/treatment-plan.utils'
import { formatToothSurfaceEntriesForDisplay } from '@utils/procedure-surface-selection.utils'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'
import PatientProfileDataTable, {
  type PatientProfileDataTableColumn,
} from '@pages/PatientProfile/shared/PatientProfileDataTable.component'
import { useAppSelector } from '@store/hooks'
import './ClinicalSummaryTreatmentPlans.css'

type ClinicalSummaryTreatmentPlansProps = {
  plans: ITreatmentPlan[]
  isLoading?: boolean
  activePlanId?: string
  onActivePlanChange?: (planId: string) => void
}

type PlanTotals = {
  charges: number
  insurance: number
  adjustments: number
  outstanding: number
}

type PhaseItemRow = IPhaseItems & {
  code: string
  description: string
}

const TX_PLAN_STATUS_BADGE_COLOR: Record<TxPlanStatus, BadgeColor> = {
  [TX_PLAN_STATUS.PROPOSED]: 'teal',
  [TX_PLAN_STATUS.ACCEPTED]: 'vivid-green',
  [TX_PLAN_STATUS.COMPLETED]: 'blue',
  [TX_PLAN_STATUS.REJECTED]: 'red',
}

function calculatePlanTotals(phases: IPhases[]): PlanTotals {
  return phases.reduce(
    (sum, phase) => {
      if (phase.is_hidden) return sum
      for (const item of phase.items ?? []) {
        if (item.is_hidden) continue
        sum.charges += item.charges ?? 0
        sum.insurance += item.insurance ?? 0
        sum.adjustments += item.adjustments ?? 0
        sum.outstanding += item.outstanding ?? 0
      }
      return sum
    },
    { charges: 0, insurance: 0, adjustments: 0, outstanding: 0 },
  )
}

function formatDeductionAmount(value: number): string {
  if (value <= 0) return formatNumber(0, true)
  return `-${formatNumber(value, true)}`
}

function formatToothSurface(item: IPhaseItems): string {
  const toothSurfaceEntries = getTSFromToothSurfaces(item.tooth_surfaces)
  const surfaceEntries = parseSurfaceString(item.surface)
  const parsedEntries =
    toothSurfaceEntries.length > 0 ? toothSurfaceEntries : surfaceEntries
  if (parsedEntries.length === 0) return '—'
  return formatToothSurfaceEntriesForDisplay(parsedEntries)
}

function formatPhaseTitle(phase: IPhases): string {
  const schedule = phase.scheduled_date
    ? `Scheduled ${new Date(phase.scheduled_date).toLocaleDateString()}`
    : 'Unscheduled'
  return `${phase.phase_name} · ${schedule}`
}

const moneyColumn = (
  key: keyof IPhaseItems,
  label: string,
): PatientProfileDataTableColumn<PhaseItemRow> => ({
  key,
  label,
  align: 'right',
  render: (item) => formatNumber(Number(item[key] ?? 0), true),
  sortValue: (item) => Number(item[key] ?? 0),
})

function ClinicalSummaryTreatmentPlans({
  plans,
  isLoading = false,
  activePlanId = '',
  onActivePlanChange,
}: ClinicalSummaryTreatmentPlansProps): React.ReactElement {
  const providerList = useAppSelector((state) => state.provider.data)
  const [openPhaseIds, setOpenPhaseIds] = useState<Record<string, string[]>>({})

  useEffect(() => {
    setOpenPhaseIds((current) => {
      const next = { ...current }
      for (const plan of plans) {
        if (next[plan.tp_id]) continue
        const firstVisiblePhase = plan.phases?.find((phase) => !phase.is_hidden)
        next[plan.tp_id] = firstVisiblePhase?.phase_id
          ? [firstVisiblePhase.phase_id]
          : []
      }
      return next
    })
  }, [plans])

  const planTabItems = useMemo(
    () =>
      plans.map((plan) => ({
        id: plan.tp_id,
        label: plan.tp_heading || 'Treatment plan',
      })),
    [plans],
  )

  const handleOpenPhasesChange = useCallback(
    (planId: string, ids: string[]): void => {
      setOpenPhaseIds((prev) => ({ ...prev, [planId]: ids }))
    },
    [],
  )

  const phaseColumns =
    useMemo((): PatientProfileDataTableColumn<PhaseItemRow>[] => {
      const providerName = (item: PhaseItemRow): string => {
        const provider = providerList.find(
          (entry) => entry.provider_id === item.provider_id,
        )
        return provider ? getFullName(provider) : '—'
      }

      return [
        { key: 'code', label: 'Code', width: '8%' },
        { key: 'description', label: 'Description', width: '25%' },
        {
          key: 'provider_id',
          label: 'Provider',
          width: '15%',
          render: providerName,
          sortValue: providerName,
        },
        {
          key: 'surface',
          label: 'T/S',
          width: '8%',
          sortable: false,
          render: formatToothSurface,
        },
        moneyColumn('charges', 'Clinic fee'),
        moneyColumn('allowed_amount', 'Allowed amt.'),
        moneyColumn('write_off', 'Write-off'),
        moneyColumn('adjustments', 'Discount'),
        moneyColumn('insurance', 'Est. ins. pay'),
        moneyColumn('outstanding', 'Est. pt. res.'),
      ]
    }, [providerList])

  const renderPlanContent = (plan: ITreatmentPlan): React.ReactElement => {
    const visiblePhases =
      plan.phases
        ?.map((phase, originalIdx) => ({ phase, originalIdx }))
        .filter(({ phase }) => !phase.is_hidden) ?? []
    const totals = calculatePlanTotals(plan.phases ?? [])
    const openIds = openPhaseIds[plan.tp_id] ?? []

    return (
      <div className="clinical-tx-plan">
        <Accordion
          className="clinical-tx-plan-phases"
          openIds={openIds}
          onOpenChange={(ids) => handleOpenPhasesChange(plan.tp_id, ids)}
        >
          {visiblePhases.map(({ phase, originalIdx }) => {
            const phaseId = phase.phase_id || `phase-${originalIdx}`
            const status = (phase.status ||
              TX_PLAN_STATUS.PROPOSED) as TxPlanStatus
            const rows: PhaseItemRow[] = (phase.items ?? [])
              .filter((item) => !item.is_hidden)
              .map((item) => ({
                ...item,
                code: item.procedure_settings?.procedure?.procedure_code ?? '',
                description:
                  item.procedure_settings?.procedure?.description ?? '',
              }))

            return (
              <div key={phaseId} className="clinical-tx-phase">
                <AccordionItem id={phaseId} title={formatPhaseTitle(phase)}>
                  {rows.length === 0 ? (
                    <p className="clinical-tx-phase-empty">
                      No procedures in this phase.
                    </p>
                  ) : (
                    <div className="clinical-tx-phase-table">
                      <PatientProfileDataTable
                        columns={phaseColumns}
                        rows={rows}
                        rowKey={(item, index) =>
                          item.item_id || `${phaseId}-${index}`
                        }
                      />
                    </div>
                  )}
                </AccordionItem>
                {/* DS AccordionItem only takes a string title and a fixed chevron, so these sit over the header. */}
                <div className="clinical-tx-phase-header-end">
                  <Badge
                    mode="tinted"
                    color={TX_PLAN_STATUS_BADGE_COLOR[status] ?? 'teal'}
                    label={status}
                  />
                  <ArrowRightIcon
                    aria-hidden="true"
                    className={`clinical-tx-phase-chevron${
                      openIds.includes(phaseId)
                        ? ' clinical-tx-phase-chevron--open'
                        : ''
                    }`}
                  />
                </div>
              </div>
            )
          })}
        </Accordion>

        <div className="clinical-tx-plan-summary">
          <div className="clinical-tx-plan-summary-row">
            <span>Total charges</span>
            <span>{formatNumber(totals.charges, true)}</span>
          </div>
          <div className="clinical-tx-plan-summary-row">
            <span>Estimate insurance</span>
            <span>{formatDeductionAmount(totals.insurance)}</span>
          </div>
          <div className="clinical-tx-plan-summary-row">
            <span>Discount</span>
            <span>{formatDeductionAmount(totals.adjustments)}</span>
          </div>
          <div className="clinical-tx-plan-summary-row clinical-tx-plan-summary-row--total">
            <span>Estimate patient responsibility</span>
            <span>{formatNumber(totals.outstanding, true)}</span>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <SectionCard className="patient-profile-section">
        <p className="patient-profile-clinical-empty">
          Loading treatment plans…
        </p>
      </SectionCard>
    )
  }

  if (plans.length === 0) {
    return (
      <SectionCard className="patient-profile-section">
        <ProfileEmptyState
          icon={TxPlanIcon}
          title="No treatment plans"
          description="You don't have any treatment plans on record."
        />
      </SectionCard>
    )
  }

  const activePlan =
    plans.find((plan) => plan.tp_id === activePlanId) ?? plans[0]

  return (
    <SectionCard className="patient-profile-section patient-profile-clinical-tx-panel">
      <ChartingTabs
        className="clinical-tx-plan-tabs"
        items={planTabItems}
        value={activePlan.tp_id}
        onChange={(planId) => onActivePlanChange?.(planId)}
      />
      {renderPlanContent(activePlan)}
    </SectionCard>
  )
}

export default ClinicalSummaryTreatmentPlans
